import React from 'react';
import {
  startOfDay,
  addDays,
  endOfDay,
  nextSaturday,
  nextSunday,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addMonths,
  format,
  subDays,
  max,
} from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faTimes } from '@fortawesome/free-solid-svg-icons';
import { PeriodsFilterValue } from '@/store/activities';
import { addYears } from 'date-fns/esm';

interface PeriodsFilterProps {
  onChange(value: PeriodsFilterValue): void;
  defaultValue?: string;
  inputDateDefaultValue?: string;
  color?: string;
}

const PeriodsFilter: React.FunctionComponent<PeriodsFilterProps> = ({
  onChange,
  defaultValue,
  inputDateDefaultValue,
  color = `secondary`,
}) => {
  const [isCustomDate, setIsCustomDate] = React.useState(
    defaultValue === `custom`,
  );
  const today = startOfDay(new Date());

  const options = React.useMemo(
    () => ({
      all: {
        label: `Evénements à venir`,
        value: {
          $gte: startOfDay(new Date()),
          $lte: endOfDay(addYears(new Date(), 1)),
        },
      },
      today: {
        label: `Aujourd’hui`,
        value: { $gte: today, $lte: endOfDay(today) },
      },
      tomorrow: {
        label: `Demain`,
        value: { $gte: addDays(today, 1), $lte: endOfDay(addDays(today, 1)) },
      },
      weekend: {
        label: `Ce week-end`,
        value: { $gte: nextSaturday(today), $lte: endOfDay(nextSunday(today)) },
      },
      week: {
        label: `Cette semaine`,
        value: {
          $gte: startOfWeek(today, { weekStartsOn: 1 }),
          $lte: endOfDay(endOfWeek(today, { weekStartsOn: 1 })),
        },
      },
      month: {
        label: `Ce mois-ci`,
        value: {
          $gte: startOfMonth(today),
          $lte: endOfMonth(today),
        },
      },
      nextMonth: {
        label: `Le mois prochain`,
        value: {
          $gte: startOfMonth(addMonths(today, 1)),
          $lte: endOfMonth(addMonths(today, 1)),
        },
      },
      custom: {
        label: `Choisir une date`,
      },
    }),
    [],
  );

  return (
    <div>
      {isCustomDate ? (
        <div className="field has-addons">
          <div className="control is-expanded">
            <input
              type="date"
              className="input"
              min={format(today, `yyyy-MM-dd`)}
              defaultValue={inputDateDefaultValue}
              onChange={(e) => {
                onChange({
                  $gte: max([
                    startOfDay(subDays(e.target.valueAsDate, 5)),
                    today,
                  ]),
                  $lte: startOfDay(addDays(e.target.valueAsDate, 5)),
                  key: `custom`,
                  inputDateValue: e.target.value,
                });
              }}
            />
          </div>
          <div className="control">
            <button
              type="submit"
              className="button"
              onClick={() => setIsCustomDate(false)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
      ) : (
        <div className="field">
          <div className="control has-icons-left">
            <div className="select">
              <select
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  if (e.target.value === `custom`) setIsCustomDate(true);
                  else
                    onChange({
                      ...options[e.target.value].value,
                      key: e.target.value,
                    });
                }}
                defaultValue={defaultValue}
              >
                {Object.keys(options).map((key) => (
                  <option value={key} key={key}>
                    {options[key].label}
                  </option>
                ))}
              </select>
              <div className="icon is-small is-left">
                <FontAwesomeIcon
                  className={`has-text-${color}`}
                  icon={faCalendar}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodsFilter;
